import { useMemo } from "react"
import { StatusBadge, DrillDownTable } from "../../widgets"
import type { ColumnDef } from "../../widgets"
import { resolveEntityStatus, } from "../../analytics-engine"
import type { TimeSeriesPeriod } from "../types"
import { format } from "date-fns"
import { DictStatus } from "@pma/types"

export function DateDrillDownContent({
  timeSeriesRaw,
  date,
  statuses,
  translate,
}: {
  timeSeriesRaw: TimeSeriesPeriod[]
  date: string
  statuses: DictStatus[]
  translate: (key: string) => string
}) {
  const { createdTasks, finishedTasks } = useMemo(() => {
    const period = timeSeriesRaw.find((p) => {
      try {
        return format(new Date(p.from), "yyyy-MM-dd") === date
      } catch {
        return false
      }
    })
    const tasks = period?.tasks || []
    const created: any[] = []
    const finished: any[] = []
    tasks.forEach((t: any) => {
      const display = resolveEntityStatus(t, statuses)
      const row = {
        id: t.id,
        taskName: t?.title || t?.name || `Task #${t.id}`,
        statusName: display.name,
        statusColor: display.color,
        statusCategory: display.category,
      }
      if (display.category === "completed") {
        finished.push(row)
      }
      created.push(row)
    })
    return { createdTasks: created, finishedTasks: finished }
  }, [timeSeriesRaw, date, statuses])

  const columns: ColumnDef[] = useMemo(
    () => [
      { header: translate("Task Name"), accessor: "taskName", sortable: true },
      {
        header: translate("status"),
        accessor: "statusName",
        sortable: true,
        render: (_val: any, row: any) => (
          <StatusBadge statusDisplay={{ name: row.statusName, color: row.statusColor }} />
        ),
      },
    ],
    [translate],
  )

  const hasData = createdTasks.length > 0 || finishedTasks.length > 0
  if (!hasData)
    return (
      <p className='py-8 text-center text-sm text-muted-foreground'>
        {translate("No tasks found")}
      </p>
    )

  return (
    <div className='flex flex-col gap-6'>
      {createdTasks.length > 0 && (
        <div>
          <h4 className='mb-2 text-sm font-semibold text-foreground'>
            {translate("created")} — {date}
            <span className='ml-2 text-xs font-normal text-muted-foreground'>
              ({createdTasks.length})
            </span>
          </h4>
          <DrillDownTable columns={columns} data={createdTasks} />
        </div>
      )}
      {finishedTasks.length > 0 && (
        <div>
          <h4 className='mb-2 text-sm font-semibold text-foreground'>
            {translate("finished")} — {date}
            <span className='ml-2 text-xs font-normal text-muted-foreground'>
              ({finishedTasks.length})
            </span>
          </h4>
          <DrillDownTable columns={columns} data={finishedTasks} />
        </div>
      )}
    </div>
  )
}
